import axios from 'axios'
import { getAuthToken } from './lib/authToken'

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'https://api.skilflow.ai' })

api.interceptors.request.use(config => {
  const token = getAuthToken()
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
    console.log(`[Skilflow API] ${config.method?.toUpperCase()} ${config.url} auth: ${token.slice(0, 16)}…`)
  } else {
    console.warn(`[Skilflow API] BLOCKED ${config.method?.toUpperCase()} ${config.url} — no token, request cancelled`)
    // Cancel the request rather than send it unauthenticated
    const controller = new AbortController()
    controller.abort()
    config.signal = controller.signal
  }
  return config
})

api.interceptors.response.use(
  response => {
    console.log(`[Skilflow API] ✓ ${response.status} ${response.config.url}`, response.data)
    return response
  },
  error => {
    console.error(
      `[Skilflow API] ✗ ${error.response?.status ?? 'ERR'} ${error.config?.url}`,
      error.response?.data ?? error.message
    )
    if (error.response?.status === 401 && !window.location.pathname.includes('/login')) {
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const listSkills = (params?: { limit?: number; offset?: number; status?: string; unassigned?: boolean }) =>
  api.get('/skills/', { params })
export const getSkill = (name: string) => api.get(`/skills/${name}`)
export const createSkill = (data: object) => api.post('/skills/', data)
export const updateSkill = (name: string, data: object) => api.put(`/skills/${name}`, data)
export const archiveSkill = (name: string) => api.delete(`/skills/${name}`)
export const permanentlyDeleteSkill = (name: string) => api.delete(`/skills/${name}/permanent`)
export const validateSkill = (name: string) => api.post(`/validate/${name}`)
export const getHistory = (name: string) => api.get(`/skills/${name}/history`)
export const publishToOpenWebUI = (name: string) => api.post(`/publish/${name}/openwebui`)
export const publishToClaude = (name: string) => api.post(`/publish/${name}/claude`)
export const publishToAll = (name: string) => api.post(`/publish/${name}/all`)

export const getGitHubConfig = () => api.get('/sync/github/config')
export const debugToken = () => api.get('/debug/token')
export const backfillVersions = () => api.post('/sync/backfill-versions', {})

export const syncFromGitHub = (repo: string = '', branch: string = 'main', token: string = '') =>
  api.post('/sync/github', { repo, branch, token }, { timeout: 120000 })

export const saveGitHubConfig = (repo: string, branch: string, token: string) =>
  api.post('/sync/config', { repo, branch, token })

export const excludeFromSync = (name: string) => api.post(`/sync/skills/${name}/exclude`)
export const getExcludedFiles = () => api.get('/sync/settings/excluded-files')

export const skillCreatorChat = (messages: {role:string;content:string}[], session_id: string) =>
  api.post('/skills/create/chat', { messages, session_id })

// Settings
export const getProfile = () => api.get('/settings/profile')
export const updateProfile = (data: { display_name: string; workspace_name: string }) =>
  api.put('/settings/profile', data)
export const completeOnboarding = () => api.post('/settings/profile/onboarding-complete')
export const getPreferences = () => api.get('/settings/preferences')
export const updatePreferences = (prefs: Record<string, boolean | string>) => api.put('/settings/preferences', { preferences: prefs })

export const editorAssist = (data: { skill_id: string; section_name: string; section_content: string; action: string; custom_message?: string }) =>
  api.post('/skills/editor/assist', data)
export const getConnections = () => api.get('/settings/connections')
export const upsertConnection = (type: string, config: object) =>
  api.put(`/settings/connections/${type}`, { config })
export const deleteConnection = (type: string) => api.delete(`/settings/connections/${type}`)
export const testConnection = (type: string, config: object) =>
  api.post(`/settings/connections/${type}/test`, { config })

// MCP API Key
export const getMCPApiKey = () => api.get('/settings/mcp/apikey')
export const generateMCPApiKey = () => api.post('/settings/mcp/apikey')
export const testMCPConnection = () => api.get('/settings/mcp/test')

// MCP OAuth
export const getMCPOAuth = () => api.get('/settings/mcp/oauth')
export const generateMCPOAuth = () => api.post('/settings/mcp/oauth')
export const getOAuthClient = () => api.get('/settings/oauth/clients')
export const createOAuthClient = () => api.post('/settings/oauth/clients')

// Comments
export const getComments = (skillId: string) => api.get(`/skills/${skillId}/comments`)
export const createComment = (skillId: string, data: { body: string; section_name?: string; parent_id?: string }) =>
  api.post(`/skills/${skillId}/comments`, data)
export const updateComment = (commentId: string, data: { body?: string; resolved?: boolean }) =>
  api.patch(`/comments/${commentId}`, data)
export const deleteComment = (commentId: string) => api.delete(`/comments/${commentId}`)

// Teams — removed in Session 4a. Stubs kept so legacy pages compile; all 404 at runtime.
export const listTeams = () => api.get('/teams/')
export const createTeam = (data: { name: string; description?: string }) => api.post('/teams/', data)
export const getTeam = (id: string) => api.get(`/teams/${id}`)
export const updateTeam = (id: string, data: { name?: string; description?: string }) => api.put(`/teams/${id}`, data)
export const deleteTeam = (id: string) => api.delete(`/teams/${id}`)
export const listTeamMembers = (id: string) => api.get(`/teams/${id}/members`)
export const inviteTeamMember = (id: string, data: { email: string; role: string }) => api.post(`/teams/${id}/members/invite`, data)
export const removeTeamMember = (id: string, userId: string) => api.delete(`/teams/${id}/members/${userId}`)
export const listTeamProjects = (teamId: string) => api.get(`/teams/${teamId}/projects`)

// Projects
export const listAllProjects = () => api.get('/projects/all')
export const createProject = (data: { name: string; description?: string; org_id?: string }) => api.post('/projects/', data)
export const assignProjectOrg = (projectId: string, orgId: string | null) =>
  api.patch(`/projects/${projectId}/org`, { org_id: orgId }).then(r => r.data)
export const getProject = (id: string) => api.get(`/projects/${id}`)
export const updateProject = (id: string, data: { name?: string; description?: string }) => api.put(`/projects/${id}`, data)
export const deleteProject = (id: string) => api.delete(`/projects/${id}`)
export const listProjectMembers = (id: string) => api.get(`/projects/${id}/members`)
export const addProjectMember = (id: string, data: { user_id: string; role: string }) => api.post(`/projects/${id}/members`, data)
export const updateProjectMemberRole = (id: string, userId: string, data: { role: string }) => api.put(`/projects/${id}/members/${userId}`, data)
export const removeProjectMember = (id: string, userId: string) => api.delete(`/projects/${id}/members/${userId}`)
export const listProjectSkills = (id: string) => api.get(`/projects/${id}/skills`)
export const addSkillToProject = (id: string, skillName: string) => api.post(`/projects/${id}/skills`, { skill_name: skillName })
export const removeSkillFromProject = (id: string, skillName: string) => api.delete(`/projects/${id}/skills/${skillName}`)

// Invites — public GETs use raw axios (no auth interceptor that blocks unauthenticated requests)
const publicApi = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'https://api.skilflow.ai' })
export const getInvite = (token: string) => publicApi.get(`/invite/${token}`)
export const acceptInvite = (token: string) => api.post(`/invite/${token}/accept`)
export const getInviteBySlug = (slug: string) => publicApi.get(`/join/${slug}`)
export const acceptInviteBySlug = (slug: string) => api.post(`/join/${slug}/accept`)

// Organizations
export const listOrgs = () => api.get('/orgs/')
export const createOrg = (data: { name: string; slug?: string }) => api.post('/orgs/', data)
export const getOrg = (id: string) => api.get(`/orgs/${id}`)
export const updateOrg = (id: string, data: { name?: string }) => api.put(`/orgs/${id}`, data)
export const listOrgMembers = (id: string) => api.get(`/orgs/${id}/members`)
export const listOrgInvitations = (id: string) => api.get(`/orgs/${id}/invitations`)
export const inviteOrgMember = (id: string, data: { email: string; role: string }) => api.post(`/orgs/${id}/invitations`, data)
export const cancelOrgInvitation = (id: string, invId: string) => api.delete(`/orgs/${id}/invitations/${invId}`)
export const getRemovalImpact = (orgId: string, userId: string) =>
  api.get(`/orgs/${orgId}/members/${userId}/impact`).then(r => r.data)

export const removeOrgMember = (orgId: string, userId: string) =>
  api.delete(`/orgs/${orgId}/members/${userId}`).then(r => r.data)
export const acceptOrgInvitation = (token: string) => api.post(`/orgs/invitations/accept?token=${encodeURIComponent(token)}`)
export const getOrgInvitePreview = (token: string) => publicApi.get(`/orgs/invitations/preview?token=${encodeURIComponent(token)}`)

// Feedback
export const submitFeedback = (data: { type: string; rating?: number; message: string }) =>
  api.post('/feedback', data)

// Activation
export const getActivationStatus = () => api.get('/activation/status')
export const dismissChecklist = () => api.post('/activation/dismiss')
export const getActivationStats = () => api.get('/activation/stats')

// Templates
export const listTemplates = () => publicApi.get('/templates/')
export const forkTemplate = (skillName: string) => api.post(`/templates/${skillName}/fork`)
export const shareAsTemplate = (skillName: string, scope: string) => api.post(`/templates/${skillName}/share`, { scope })
export const listOrgTemplates = () => api.get('/templates/org')
export const orgTemplateCount = () => api.get('/templates/org/count')
export const forkOrgTemplate = (skillName: string) => api.post(`/templates/org/${skillName}/fork`)
export const generateSection = (data: { skill_name: string; section_title: string; section_content: string; user_context: string }) =>
  api.post('/templates/generate-section', data)

// Analytics
export const trackEvent = (event_type: string, skill_name?: string, metadata?: Record<string, any>) =>
  api.post('/analytics/event', { event_type, skill_name, metadata }).catch(() => {})
export const getAnalyticsSummary = () => api.get('/analytics/summary')

export default api
